// ⚠️  FILE GENERATO AUTOMATICAMENTE — NON MODIFICARE A MANO.
// Fonte unica: shared/puntiRitiro.js (vedi lì per spiegazione e commenti).
// Rigenerato da scripts/sync-shared-rules.js ad ogni build/deploy.
// Se hai bisogno di cambiare questa logica, modifica SOLO shared/puntiRitiro.js.

/**
 * shared/puntiRitiro.js — FONTE UNICA della logica "punti di ritiro e
 * riconsegna con supplementi".
 *
 * Come bookingRules.js, questo file NON viene importato direttamente: lo
 * script scripts/sync-shared-rules.js lo copia in
 *   - src/utils/puntiRitiro.generated.js   (frontend)
 *   - functions/puntiRitiro.generated.js   (Cloud Functions)
 * Modifica SOLO questo file; le copie si rigenerano con `npm run sync-shared`
 * (gira da solo a ogni start/build/deploy).
 *
 * Perché qui e non duplicato come calculateBookingPrice/calculateBookingPriceServer:
 * la regola dei supplementi decide quanto viene addebitato. Tenerla in un
 * unico file elimina il rischio che browser e server divergano. Il SERVER
 * resta comunque l'unica fonte di verità: il browser usa queste funzioni solo
 * per mostrare l'anteprima dei costi.
 *
 * Modello dati (companies/{companyId}/settings/puntiRitiroRiconsegna):
 *   { [id]: { id, nome, indirizzo, lat, lng, supplementoRitiro,
 *             supplementoRiconsegna, attivo, note, ordine } }
 * La SEDE non è salvata lì: è un punto virtuale (id riservato "sede"),
 * sempre presente e sempre gratuito, costruito dai dati aziendali.
 *
 * Importi: i supplementi sono in euro nel database, ma TUTTO il calcolo
 * avviene in centesimi interi (eurosToCents) per evitare differenze tra il
 * totale mostrato e quello addebitato da Stripe.
 *
 * Sintassi: CommonJS puro (come bookingRules.js).
 */

const SEDE_ID = 'sede';
// Tetto "ragionevole" per un singolo supplemento (euro). Applicato sia dalle
// Rules del database sia da normalizePunti (difesa in profondità).
const MAX_SUPPLEMENTO_EURO = 500;

/** Euro (number|string) → centesimi interi. Valori non validi o negativi → 0. */
function eurosToCents(value) {
  const n = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // toPrecision(12) toglie il rumore binario (1.005*100 = 100.49999999999999)
  // prima dell'arrotondamento, così 1.005 € → 101 cent come si aspetta l'admin.
  return Math.round(parseFloat((n * 100).toPrecision(12)));
}

/** Centesimi → euro (number a 2 decimali), solo per visualizzazione/salvataggio. */
function centsToEuros(cents) {
  return Math.round(Number(cents) || 0) / 100;
}

function cleanString(v, max) {
  if (typeof v !== 'string') return '';
  const t = v.trim();
  return typeof max === 'number' ? t.slice(0, max) : t;
}

function cleanCoord(v) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/**
 * Accetta il nodo così com'è nel database (oggetto indicizzato per id, oppure
 * array) e restituisce un array ordinato di punti puliti. Scarta: id
 * riservato "sede", voci senza nome, voci non-oggetto. Supplementi fuori
 * range vengono riportati nel range (0..MAX) invece di far fallire tutto.
 */
function normalizePunti(node) {
  if (!node || typeof node !== 'object') return [];
  const entries = Array.isArray(node)
    ? node.map((v, i) => [v && v.id ? String(v.id) : String(i), v])
    : Object.entries(node);

  const out = [];
  for (const [key, raw] of entries) {
    if (!raw || typeof raw !== 'object') continue;
    const id = cleanString(raw.id || key, 100);
    if (!id || id === SEDE_ID) continue;
    const nome = cleanString(raw.nome, 80);
    if (!nome) continue;
    const clampEuro = (v) => Math.min(MAX_SUPPLEMENTO_EURO, centsToEuros(eurosToCents(v)));
    out.push({
      id,
      nome,
      indirizzo: cleanString(raw.indirizzo, 200),
      lat: cleanCoord(raw.lat),
      lng: cleanCoord(raw.lng),
      supplementoRitiro: clampEuro(raw.supplementoRitiro),
      supplementoRiconsegna: clampEuro(raw.supplementoRiconsegna),
      attivo: raw.attivo !== false, // default: attivo, come un punto appena creato
      note: cleanString(raw.note, 300),
      ordine: Number.isFinite(Number(raw.ordine)) ? Number(raw.ordine) : 0,
    });
  }
  out.sort((a, b) => (a.ordine - b.ordine) || a.nome.localeCompare(b.nome, 'it'));
  return out;
}

/** Punti aggiuntivi attivi (quelli che il cliente può davvero scegliere). */
function getActivePunti(node) {
  return normalizePunti(node).filter((p) => p.attivo);
}

/**
 * Punto virtuale "Sede": sempre presente, gratuito, non eliminabile.
 * Indirizzo costruito da azienda.indirizzo + cap + città (obbligatori per legge).
 */
function buildSedePoint(azienda) {
  const a = azienda || {};
  const via = cleanString(a.indirizzo, 200);
  const capCitta = [cleanString(a.cap, 10), cleanString(a.citta, 80)].filter(Boolean).join(' ');
  return {
    id: SEDE_ID,
    nome: 'Sede',
    indirizzo: [via, capCitta].filter(Boolean).join(', '),
    lat: cleanCoord(a.lat),
    lng: cleanCoord(a.lng),
    supplementoRitiro: 0,
    supplementoRiconsegna: 0,
    attivo: true,
    note: '',
    ordine: -1,
    isSede: true,
  };
}

function isSedeId(id) {
  return id === undefined || id === null || id === '' || id === SEDE_ID;
}

/**
 * Risolve i punti scelti e calcola i supplementi IN CENTESIMI.
 *
 * @param {object} args
 * @param {object|Array|null} args.punti       nodo grezzo puntiRitiroRiconsegna
 * @param {object} args.azienda                settings/azienda (per la sede)
 * @param {string} [args.ritiroId]             vuoto/undefined/"sede" = sede
 * @param {string} [args.riconsegnaId]         idem
 * @returns {{ok:true, ritiro, riconsegna, supplementoRitiroCents,
 *            supplementoRiconsegnaCents, supplementoTotaleCents}
 *          |{ok:false, field:'ritiro'|'riconsegna', code:string, message:string}}
 *
 * Un id non presente nella lista ATTIVA (inventato, cancellato o
 * disattivato) è sempre un errore: non si ricade mai silenziosamente sulla
 * sede, altrimenti il cliente crederebbe di ritirare in aeroporto.
 */
function computeSupplements({ punti, azienda, ritiroId, riconsegnaId } = {}) {
  const active = getActivePunti(punti);
  const sede = buildSedePoint(azienda);

  const resolve = (id, field) => {
    if (isSedeId(id)) return { point: sede, cents: 0 };
    if (typeof id !== 'string') {
      return { error: { ok: false, field, code: 'punto-non-valido', message: 'Punto di ' + (field === 'ritiro' ? 'ritiro' : 'riconsegna') + ' non valido.' } };
    }
    const p = active.find((x) => x.id === id);
    if (!p) {
      return {
        error: {
          ok: false, field, code: 'punto-non-disponibile',
          message: 'Il punto di ' + (field === 'ritiro' ? 'ritiro' : 'riconsegna') + ' selezionato non è più disponibile. Scegline un altro.',
        },
      };
    }
    const cents = eurosToCents(field === 'ritiro' ? p.supplementoRitiro : p.supplementoRiconsegna);
    return { point: p, cents };
  };

  const r = resolve(ritiroId, 'ritiro');
  if (r.error) return r.error;
  const c = resolve(riconsegnaId, 'riconsegna');
  if (c.error) return c.error;

  return {
    ok: true,
    ritiro: r.point,
    riconsegna: c.point,
    supplementoRitiroCents: r.cents,
    supplementoRiconsegnaCents: c.cents,
    supplementoTotaleCents: r.cents + c.cents,
  };
}

/**
 * Totale in centesimi: (noleggio − sconto) + supplementi.
 * Lo sconto si applica SOLO al noleggio e non lo porta sotto zero.
 * (L'importo minimo del codice sconto è controllato in reserveDiscountServer
 * sul solo prezzo del noleggio, prima di arrivare qui.)
 */
function composeTotalCents({ prezzoBase, discountAmount, supplementoTotaleCents }) {
  const noleggioCents = Math.max(0, eurosToCents(prezzoBase) - eurosToCents(discountAmount));
  return noleggioCents + Math.max(0, Math.round(Number(supplementoTotaleCents) || 0));
}

/** Testo "Nome — indirizzo" salvato in booking.luogoRitiro/luogoRiconsegna. */
function formatLuogo(point) {
  if (!point) return '';
  const nome = point.nome || '';
  const ind = point.indirizzo || '';
  if (nome && ind) return nome + ' — ' + ind;
  return nome || ind;
}

/** Link Google Maps per un punto (usa coordinate se presenti, altrimenti indirizzo). */
function mapsLink(point) {
  if (!point) return '';
  if (Number.isFinite(point.lat) && Number.isFinite(point.lng)) {
    return 'https://www.google.com/maps/search/?api=1&query=' + point.lat + ',' + point.lng;
  }
  const q = point.indirizzo || point.nome;
  return q ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q) : '';
}

const ALIQUOTA_IVA_DEFAULT = 22;

/**
 * Righe di fattura: noleggio (netto dello sconto) + supplementi ritiro e
 * riconsegna come righe SEPARATE, tutte con la stessa aliquota IVA del
 * noleggio. Gli importi sono IVA inclusa (come i prezzi del sito); imponibile
 * e imposta sono scorporati per riga in centesimi. La somma delle righe è
 * SEMPRE uguale a prezzoTotale (quanto addebitato).
 * Prenotazioni senza supplementi → una sola riga "noleggio" = prezzoTotale
 * (identico a prima).
 */
function buildInvoiceLines(booking, aliquotaIva) {
  const aliquota = Number.isFinite(Number(aliquotaIva)) ? Number(aliquotaIva) : ALIQUOTA_IVA_DEFAULT;
  const b = booking || {};
  const totalCents = eurosToCents(b.prezzoTotale);
  const supRitCents = eurosToCents(b.supplementoRitiro);
  const supRicCents = eurosToCents(b.supplementoRiconsegna);
  // Sicurezza: i supplementi non possono superare il totale.
  const supRit = Math.min(supRitCents, totalCents);
  const supRic = Math.min(supRicCents, totalCents - supRit);
  const lines = [
    { codice: 'noleggio', descrizione: 'Noleggio veicolo', importoCents: totalCents - supRit - supRic },
  ];
  if (supRit > 0) lines.push({ codice: 'supplemento_ritiro', descrizione: 'Supplemento punto di ritiro', importoCents: supRit });
  if (supRic > 0) lines.push({ codice: 'supplemento_riconsegna', descrizione: 'Supplemento punto di riconsegna', importoCents: supRic });
  return lines.map((l) => {
    const imponibileCents = Math.round(l.importoCents / (1 + aliquota / 100));
    // Object.assign e non lo spread: in CRA Babel trasforma lo spread con un
    // `import` degli helper, rendendo il file CommonJS "misto" e rompendo gli
    // import con nome in webpack. Qui niente spread di oggetti/array.
    return Object.assign({}, l, {
      aliquotaIva: aliquota,
      importo: centsToEuros(l.importoCents),
      imponibile: centsToEuros(imponibileCents),
      imposta: centsToEuros(l.importoCents - imponibileCents),
    });
  });
}

module.exports = {
  ALIQUOTA_IVA_DEFAULT,
  buildInvoiceLines,
  SEDE_ID,
  MAX_SUPPLEMENTO_EURO,
  eurosToCents,
  centsToEuros,
  normalizePunti,
  getActivePunti,
  buildSedePoint,
  isSedeId,
  computeSupplements,
  composeTotalCents,
  formatLuogo,
  mapsLink,
};
